import {AxiosHeaders} from 'axios';
import CryptoJS from 'crypto-js';

import {apiUrlV2} from '../constants';

interface GenerateHeadersOptions {
  secret: string;
  keyId: string;
  requestBody: unknown;
  method?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  host?: string;
}

/**
 * Generates authentication headers for Cashia API requests
 * @param options - Configuration object for header generation
 * @returns AxiosHeaders with Cashia authentication
 */
export const generateCashiaHeadersV2 = ({
  secret,
  keyId,
  requestBody,
  method = 'POST',
  host = apiUrlV2.replace('/api', ''),
}: GenerateHeadersOptions): AxiosHeaders => {
  const nonce = generateNonce();
  const timestamp = Math.floor(Date.now() / 1000);

  const bodyHash = generateBodyHash(requestBody, secret);
  const signature = generateSignature({
    host,
    method,
    timestamp,
    nonce,
    keyId,
    secret,
  });

  const headers = new AxiosHeaders();
  headers.set('Content-Type', 'application/json');
  headers.set('X-Cashia-Key-ID', keyId);
  headers.set('X-Cashia-Timestamp', timestamp);
  headers.set('X-Cashia-Signature', signature);
  headers.set('X-Cashia-Hash', bodyHash);
  headers.set('X-Cashia-Nonce', nonce);

  return headers;
};

/**
 * Generates a random nonce for request uniqueness
 * @returns Random alphanumeric string
 */
const generateNonce = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Generates HMAC SHA256 hash of request body
 * @param requestBody - The request payload
 * @param secret - Secret key for hashing
 * @returns Hexadecimal hash string
 */
const generateBodyHash = (requestBody: unknown, secret: string): string => {
  return CryptoJS.HmacSHA256(JSON.stringify(requestBody), secret).toString(
    CryptoJS.enc.Hex
  );
};

interface SignatureOptions {
  host: string;
  method: string;
  timestamp: number;
  nonce: string;
  keyId: string;
  secret: string;
}

/**
 * Generates HMAC SHA256 signature for request authentication
 * @param options - Signature generation parameters
 * @returns Hexadecimal signature string
 */
const generateSignature = ({
  host,
  method,
  timestamp,
  nonce,
  keyId,
  secret,
}: SignatureOptions): string => {
  const signingString = `${host}${method}${timestamp}${nonce}${keyId}`;
  return CryptoJS.HmacSHA256(signingString, secret).toString(CryptoJS.enc.Hex);
};

/**
 * Validates if required authentication parameters are present
 * @param keyId - API Key ID
 * @param secret - Secret Key
 * @param terminalId - Terminal ID
 * @throws Error if parameters are missing
 */
export const validateAuthParamsV2 = (keyId: string, secret: string): void => {
  if (!keyId || !secret) {
    throw new Error(
      'Authentication credentials (keyId and secret) are required'
    );
  }
};

/**
 * Creates a plain object of Cashia headers (useful for fetch API)
 * @param options - Configuration object for header generation
 * @returns Plain object with header key-value pairs
 */
export const generateCashiaHeadersObject = (
  options: GenerateHeadersOptions
): Record<string, string | number> => {
  const headers = generateCashiaHeadersV2(options);
  const headersObject: Record<string, string | number> = {};

  // Convert AxiosHeaders to plain object
  Object.keys(headers).forEach((key) => {
    const value = headers.get(key);
    if (value !== null && value !== undefined) {
      // Convert value to string or number
      if (typeof value === 'string' || typeof value === 'number') {
        headersObject[key] = value;
      } else if (Array.isArray(value)) {
        // Join arrays into comma-separated string
        headersObject[key] = value.join(', ');
      } else {
        // Convert other types to string
        headersObject[key] = String(value);
      }
    }
  });

  return headersObject;
};
